import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';

const plotPoints = [
  { x: 80, y: 220 },
  { x: 300, y: 120 },
  { x: 520, y: 170 },
  { x: 760, y: 88 },
  { x: 1030, y: 132 }
];

const desktopPath = `M ${plotPoints[0].x} ${plotPoints[0].y} C 190 110, 250 100, ${plotPoints[1].x} ${plotPoints[1].y} C 390 140, 450 210, ${plotPoints[2].x} ${plotPoints[2].y} C 650 110, 700 70, ${plotPoints[3].x} ${plotPoints[3].y} C 890 104, 960 170, ${plotPoints[4].x} ${plotPoints[4].y}`;
const marqueeWords = ['Clarity First', 'Design Precision', 'Clean Code', 'Fast Delivery', 'Long-Term Support'];

function ProcessFlow({ steps }) {
  const stageRef = useRef(null);
  const activePathRef = useRef(null);
  const nodeRefs = useRef([]);
  const cardRefs = useRef([]);
  const scannerRef = useRef(null);
  const marqueeTrackRef = useRef(null);

  useEffect(() => {
    if (!stageRef.current || !activePathRef.current || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const pathLength = activePathRef.current.getTotalLength();

    gsap.set(activePathRef.current, { strokeDasharray: pathLength, strokeDashoffset: pathLength });
    gsap.set(nodeRefs.current, { scale: 0.35, opacity: 0.42, transformOrigin: 'center center' });
    gsap.set(cardRefs.current, { y: 26, opacity: 0 });

    const intro = gsap.timeline({ defaults: { ease: 'power3.out' } });
    intro
      .to(activePathRef.current, { strokeDashoffset: 0, duration: 1.15 })
      .to(nodeRefs.current, { scale: 1, opacity: 1, duration: 0.38, stagger: 0.08 }, '-=0.45')
      .to(cardRefs.current, { y: 0, opacity: 1, duration: 0.55, stagger: 0.07 }, '-=0.35');

    gsap.to(nodeRefs.current, {
      y: -3,
      duration: 1.5,
      stagger: 0.12,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut'
    });

    gsap.to(scannerRef.current, {
      xPercent: 130,
      duration: 7.5,
      repeat: -1,
      ease: 'none'
    });

    if (marqueeTrackRef.current) {
      gsap.to(marqueeTrackRef.current, {
        xPercent: -50,
        duration: 18,
        repeat: -1,
        ease: 'none'
      });
    }

    return () => {
      intro.kill();
      gsap.killTweensOf(nodeRefs.current);
      gsap.killTweensOf(scannerRef.current);
      gsap.killTweensOf(marqueeTrackRef.current);
    };
  }, []);

  return (
    <section id="process" className="section-space relative overflow-hidden bg-[#ececea]" data-aos="fade-up">
      <div className="section-wrap relative">
        <div className="mb-10 grid grid-cols-1 gap-7 md:mb-14 md:grid-cols-12 md:items-end">
          <div className="md:col-span-5">
            <p className="meta-label">02 / Work Process</p>
            <h2 className="mt-4 font-display text-4xl font-bold uppercase leading-[0.85] tracking-[-0.03em] md:text-7xl">
              Precision
              <br />
              Pipeline
            </h2>
          </div>
          <p className="copy-refined max-w-2xl text-sm text-ink/74 md:col-span-7 md:text-base">
            This is not random execution. Each phase compounds into the next with clear milestones, speed, and visual
            quality control. You get progress visibility from day one and smooth delivery to launch.
          </p>
        </div>

        <div ref={stageRef} className="relative overflow-hidden rounded-[2rem] border border-ink/15 bg-snow/80 p-5 shadow-ambient md:p-9">
          <div
            className="pointer-events-none absolute -left-1/2 top-0 h-full w-[36%] bg-gradient-to-r from-transparent via-white/65 to-transparent opacity-55"
            ref={scannerRef}
            aria-hidden="true"
          />

          <div className="pointer-events-none absolute inset-0 opacity-40 [background-image:radial-gradient(circle_at_1px_1px,rgba(16,16,16,0.16)_1px,transparent_0)] [background-size:22px_22px]" />

          <svg className="pointer-events-none hidden w-full md:block" viewBox="0 0 1120 290" role="img" aria-label="Work process trajectory">
            <path d={desktopPath} fill="none" stroke="rgba(16,16,16,0.16)" strokeDasharray="4 8" strokeWidth="1.6" />
            <path ref={activePathRef} d={desktopPath} fill="none" stroke="rgba(16,16,16,0.82)" strokeWidth="2.8" />
            {plotPoints.map((point, index) => (
              <g
                key={steps[index]?.id || `${point.x}-${point.y}`}
                ref={(element) => {
                  nodeRefs.current[index] = element;
                }}
              >
                <circle cx={point.x} cy={point.y} fill="rgba(16,16,16,0.92)" r="9" />
                <circle cx={point.x} cy={point.y} fill="none" r="16" stroke="rgba(16,16,16,0.28)" strokeWidth="1.2" />
              </g>
            ))}
          </svg>

          <div className="mt-2 hidden grid-cols-5 gap-4 md:grid">
            {steps.map((step, index) => (
              <article
                key={step.id}
                ref={(element) => {
                  cardRefs.current[index] = element;
                }}
                className={`ui-card-hover rounded-2xl border border-ink/12 bg-snow p-4 ${index % 2 === 0 ? 'md:-mt-1' : 'md:mt-8'}`}
              >
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/50">0{index + 1}</p>
                <h3 className="mt-2 text-sm font-semibold uppercase tracking-[0.13em] text-ink">{step.title}</h3>
                <p className="copy-refined mt-3 text-xs text-ink/65">{step.detail}</p>
              </article>
            ))}
          </div>

          <div className="space-y-4 md:hidden">
            {steps.map((step, index) => (
              <motion.article
                key={step.id}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{ duration: 0.42, delay: index * 0.04 }}
                className="ui-card-hover rounded-2xl border border-ink/12 bg-snow p-4"
              >
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/50">0{index + 1}</p>
                <h3 className="mt-2 text-sm font-semibold uppercase tracking-[0.13em] text-ink">{step.title}</h3>
                <p className="copy-refined mt-3 text-sm text-ink/70">{step.detail}</p>
              </motion.article>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-3 md:mt-10 md:grid-cols-3">
            <div className="ui-card-hover rounded-2xl border border-ink/12 bg-white p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-ink/50">Avg Delivery</p>
              <p className="mt-2 font-display text-3xl font-bold tracking-[-0.02em]">1-2 Weeks</p>
            </div>
            <div className="ui-card-hover rounded-2xl border border-ink/12 bg-white p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-ink/50">Communication</p>
              <p className="mt-2 font-display text-3xl font-bold tracking-[-0.02em]">Weekly Sync</p>
            </div>
            <div className="ui-card-hover rounded-2xl border border-ink/12 bg-white p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-ink/50">Response Time</p>
              <p className="mt-2 font-display text-3xl font-bold tracking-[-0.02em]">Within 24h</p>
            </div>
          </div>

          <div className="mt-8 overflow-hidden rounded-[1.65rem] border border-ink/12 bg-coal px-0 py-5 text-white md:py-6">
            <div className="mb-4 px-5 md:px-7">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/55">Working Style</p>
              <p className="mt-2 max-w-2xl text-sm uppercase tracking-[0.12em] text-white/85 md:text-base">
                Structured execution with a premium, smooth client experience from kickoff to final handoff.
              </p>
            </div>
            <div className="overflow-hidden border-y border-white/12 bg-snow/[0.03]">
              <div ref={marqueeTrackRef} className="flex w-max items-center gap-8 px-5 py-3 md:gap-10 md:px-7">
                {[...marqueeWords, ...marqueeWords].map((word, index) => (
                  <div key={`${word}-${index}`} className="flex items-center gap-3 whitespace-nowrap">
                    <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/88">{word}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProcessFlow;
